/* eslint-disable no-restricted-imports */
import * as React from "react";
import { ToggleGroup as ToggleGroupPrimitive } from "@base-ui/react/toggle-group";
import { Toggle } from "@base-ui/react/toggle";
/* eslint-enable no-restricted-imports */
import classNames from "classnames";
import styles from "./ToggleGroup.module.scss";

export type ToggleGroupSize = "sm" | "md" | "lg";

export type ToggleGroupVariant = "segmented" | "button";

/**
 * `segmented` is the gray pill track and takes a `size`. `button` is a joined
 * row of 28px Button-primary squares, one size only.
 */
type ToggleGroupVariantProps =
    | { variant?: "segmented"; size?: ToggleGroupSize }
    | { variant: "button"; size?: never };

type ToggleGroupRootProps<Value extends string> = Omit<
    React.ComponentProps<typeof ToggleGroupPrimitive>,
    "value" | "onValueChange" | "defaultValue"
> &
    ToggleGroupVariantProps & {
        value?: Value | null;
        onValueChange?: (value: Value | null) => void;
    };

const ToggleGroupVariantContext =
    React.createContext<ToggleGroupVariant>("segmented");

// eslint-disable-next-line react-refresh/only-export-components
const ToggleGroupRoot = <Value extends string>({
    value,
    onValueChange,
    variant = "segmented",
    size = "md",
    className,
    children,
    ...props
}: Readonly<ToggleGroupRootProps<Value>>) => {
    const handleValueChange = React.useCallback(
        (groupValue: Value[]) => {
            onValueChange?.(groupValue[groupValue.length - 1] ?? null);
        },
        [onValueChange],
    );

    const isButtonVariant = variant === "button";

    return (
        <ToggleGroupVariantContext.Provider value={variant}>
            <ToggleGroupPrimitive
                value={value != null ? [value] : []}
                onValueChange={handleValueChange}
                className={classNames(
                    isButtonVariant ? styles.buttonRoot : styles.root,
                    !isButtonVariant && {
                        [styles.sm]: size === "sm",
                        [styles.md]: size === "md",
                        [styles.lg]: size === "lg",
                    },
                    className,
                )}
                {...props}
            >
                {children}
            </ToggleGroupPrimitive>
        </ToggleGroupVariantContext.Provider>
    );
};

interface ToggleGroupItemProps<Value extends string> extends Omit<
    React.ComponentProps<typeof Toggle<Value>>,
    "value"
> {
    value: Value;
}

// eslint-disable-next-line react-refresh/only-export-components
const ToggleGroupItem = <Value extends string>({
    value,
    className,
    children,
    ...props
}: Readonly<ToggleGroupItemProps<Value>>) => {
    const variant = React.useContext(ToggleGroupVariantContext);

    return (
        <Toggle
            value={value}
            className={classNames(
                variant === "button" ? styles.buttonItem : styles.item,
                className,
            )}
            {...props}
        >
            {children}
        </Toggle>
    );
};

export const ToggleGroup = {
    Root: ToggleGroupRoot,
    Item: ToggleGroupItem,
};
